import React from 'react';

/**
 * Renders a <table> on md+ screens and stacked cards on phones.
 *
 * columns: Array<{
 *   key: string,
 *   label: string,
 *   render: (row) => ReactNode,
 *   card: 'header' | 'body' | 'footer' | false,
 *     'header'  → shown prominently at top of each mobile card
 *     'body'    → shown in a 2-col label/value grid in the card middle
 *     'footer'  → shown at card bottom (use for action buttons)
 *     false     → table-only column, hidden in card view
 *   align: 'left' | 'right',          (default: 'left')
 *   stopPropagation: bool,             (prevent row-click in this cell)
 *   headerClassName: string,
 *   cellClassName: string,
 * }>
 *
 * data:          array of row objects
 * keyExtractor:  (row) => string | number  (defaults to row._id ?? row.id)
 * onRowClick:    (row) => void
 * emptyState:    ReactNode shown when data is empty
 * tableMinWidth: Tailwind min-w class (default 'min-w-[800px]')
 */
const ResponsiveDataTable = ({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyState,
  tableMinWidth = 'min-w-[800px]',
}) => {
  const getKey = (row) => (keyExtractor ? keyExtractor(row) : (row._id ?? row.id));

  const headerCol  = columns.find(c => c.card === 'header');
  const bodyColumns = columns.filter(c => c.card === 'body');
  const footerCol  = columns.find(c => c.card === 'footer');

  if (!data.length && emptyState) return <>{emptyState}</>;

  return (
    <>
      {/* ── Desktop table (md+) ── */}
      <div className="hidden md:block overflow-x-auto">
        <table className={`w-full ${tableMinWidth}`}>
          <thead>
            <tr className="border-b border-neutral-100">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`py-3.5 px-5 text-sm font-medium text-neutral-500 ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  } ${col.headerClassName || ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(row => (
              <tr
                key={getKey(row)}
                className={`border-b border-neutral-50 transition-colors ${
                  onRowClick ? 'hover:bg-neutral-50/60 cursor-pointer' : ''
                }`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map(col => (
                  <td
                    key={col.key}
                    className={`py-4 px-5 ${col.align === 'right' ? 'text-right' : ''} ${col.cellClassName || ''}`}
                    onClick={col.stopPropagation ? (e) => e.stopPropagation() : undefined}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile cards (below md) ── */}
      <div className="md:hidden divide-y divide-neutral-100">
        {data.map(row => (
          <div
            key={getKey(row)}
            className={`p-4 ${onRowClick ? 'cursor-pointer active:bg-neutral-50' : ''}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {headerCol && (
              <div className="mb-3">
                {headerCol.render(row)}
              </div>
            )}

            {bodyColumns.length > 0 && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-3">
                {bodyColumns.map(col => (
                  <div key={col.key}>
                    <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide mb-0.5">
                      {col.label}
                    </p>
                    <div className="text-sm text-neutral-700">{col.render(row)}</div>
                  </div>
                ))}
              </div>
            )}

            {footerCol && (
              <div
                className="flex items-center justify-end gap-1 pt-2 border-t border-neutral-100"
                onClick={(e) => e.stopPropagation()}
              >
                {footerCol.render(row)}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default ResponsiveDataTable;
